import client from './client';

/**
 * Upload image and analyze quality
 * @param {File} file - Image file to upload
 * @param {Object} metadata - Upload metadata (brand, itemCode, type, sortOrder)
 * @param {Function} onProgress - Progress callback
 * @returns {Promise<Object>} Analysis results
 */
export async function uploadAndAnalyze(file, metadata, onProgress) {
  const formData = new FormData();
  formData.append('image', file);
  formData.append('brand', metadata.brand);
  formData.append('itemCode', metadata.itemCode);
  formData.append('type', metadata.type);
  
  // Add sortOrder if provided
  if (metadata.sortOrder) {
    formData.append('sortOrder', metadata.sortOrder);
  }

  try {
    const response = await client.post(
      '/api/image-quality/upload',
      formData,
      {
        // Delete the default application/json Content-Type so the browser sets
        // multipart/form-data with the correct boundary automatically.
        headers: { 'Content-Type': undefined },
        timeout: 120000, // 2 min for large images
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          if (onProgress) {
            onProgress(percentCompleted);
          }
        }
      }
    );

    return response;
  } catch (error) {
    const msg = error.data?.error || error.message
      || (error instanceof AggregateError ? error.errors?.map(e => e.message).join('; ') : null)
      || 'Upload failed';
    throw { message: msg, response: error };
  }
}

/**
 * Analyze existing image on S3
 * @param {string} bucket - S3 bucket name
 * @param {string} key - S3 object key
 * @returns {Promise<Object>} Analysis results
 */
export async function analyzeExisting(bucket, key) {
  return client.post('/api/image-quality/analyze', { bucket, key });
}

/**
 * Delete an image record
 * @param {string} itemCode - Item code
 * @param {string} imageType - Image type
 * @param {number} sortOrder - Sort order
 * @returns {Promise<Object>} Delete result
 */
export async function deleteImage(itemCode, imageType, sortOrder) {
  try {
    return await client.delete(
      `/api/image-quality/records/${itemCode}/${imageType}/${sortOrder}`
    );
  } catch (error) {
    throw {
      message: error.data?.error || error.message || 'Delete failed',
      response: error
    };
  }
}

/**
 * Update sort order for an image record
 * @param {string} itemCode - Item code
 * @param {string} imageType - Image type
 * @param {number} oldSortOrder - Current sort order
 * @param {number} newSortOrder - New sort order
 * @returns {Promise<Object>} Update result
 */
export async function updateSortOrder(itemCode, imageType, oldSortOrder, newSortOrder) {
  try {
    return await client.patch(
      `/api/image-quality/records/${itemCode}/${imageType}/${oldSortOrder}/sort-order`,
      { newSortOrder }
    );
  } catch (error) {
    throw {
      message: error.data?.error || error.message || 'Update failed',
      response: error
    };
  }
}

/**
 * Analyze an existing image (on-demand analysis)
 * @param {string} itemCode - Item code
 * @param {string} imageType - Image type
 * @param {number} sortOrder - Sort order
 * @returns {Promise<Object>} Analysis results
 */
export async function analyzeExistingImage(itemCode, imageType, sortOrder = 1) {
  try {
    return await client.post('/api/image-quality/analyze-existing', {
      itemCode,
      imageType,
      sortOrder
    });
  } catch (error) {
    throw {
      message: error.data?.error || error.message || 'Analysis failed',
      response: error
    };
  }
}
